/**
 * The Profile / Scoreboard screen — the meta-progression UI (docs/GAME_DESIGN.md
 * §19.3 / §20). A full-screen neon overlay with two tabs: **UNLOCKS** (the Flux
 * balance + the unlock-tree node graph) and **SCOREBOARD** (the personal-best
 * grid + run history). Plus a small **launcher** drawn on the title screen and
 * the game-over screen, and the run-end Flux award.
 *
 * Render-/input-only: this module reads the {@link Profile} and `GameState`
 * but never writes the simulation. The profile is meta — it is not in `state`
 * and the sim never reads it. This is the OVERVOLT neon-polish render pass:
 * animated affordable-node pulses, branch-coloured node graph + icons, flowing
 * prerequisite traces, a purchase flash, and a counting-up run-end Flux tally.
 */
import { CONFIG, type UnlockGrant } from '../config';
import type { PointerPos } from '../engine/input';
import { clamp, roundRect, withAlpha } from '../engine/util';
import { t, t as tr } from '../i18n';
import {
  DIFFICULTIES,
  RUN_MODES,
  bestFor,
  isDifficultyUnlocked,
  isModeUnlocked,
  type Profile,
  type RunRecord,
} from '../game/profile';
import type { GameState } from '../game/state';

/** Which tab of the Profile screen is showing. */
export type ProfileTab = 'unlocks' | 'scoreboard';

/** A clickable rectangle. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The outcome of a click inside the open Profile overlay. */
export type ProfileHit =
  | { kind: 'close' }
  | { kind: 'tab'; tab: ProfileTab }
  | { kind: 'unlock'; id: string }
  // Sprint 62 (auth-frontend-impl-s62) — signed-in players see a small
  // Sign-out button row at the bottom-left of the panel. The orchestrator
  // routes `'signout'` to {@link Game.signOut}, which resets the modal
  // gate (`Profile.guestMode = null`) so the next render re-opens the
  // start-screen modal. Guests never see the button, so a guest click
  // never produces this hit.
  | { kind: 'signout' };

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';
/** Flux's electric-violet accent — distinct from gold and the cyan score (§11). */
const FLUX = '#b388ff';
/** Gold — reused for the "new best" star highlight. */
const GOLD = '#f7d51d';

/** Per-branch accent colour for the unlock node graph (§11 — distinct branches). */
const BRANCH_COLOR: Record<string, string> = {
  arsenal: C.accent, // cyan
  modes: C.good, // green
  challenge: C.warning, // orange
  cosmetic: C.core, // pink
};

// --- overlay geometry --------------------------------------------------------

const PANEL: Rect = { x: 40, y: 28, w: 720, h: 504 };
const CLOSE_RECT: Rect = { x: PANEL.x + PANEL.w - 42, y: PANEL.y + 16, w: 26, h: 26 };
const TAB_UNLOCKS: Rect = { x: PANEL.x + 28, y: PANEL.y + 62, w: 156, h: 30 };
const TAB_SCORES: Rect = { x: PANEL.x + 192, y: PANEL.y + 62, w: 156, h: 30 };
/**
 * Sprint 62 — bottom-left Sign-out button. Drawn only when the player is
 * signed in (`Profile.guestMode === false`); guests never see it. Sits
 * next to the footer closeHint so it never collides with the unlock-tree
 * graph above.
 */
const SIGNOUT_RECT: Rect = {
  x: PANEL.x + 28,
  y: PANEL.y + PANEL.h - 36,
  w: 112,
  h: 24,
};

/**
 * The title-screen Profile launcher button — docked just below the HUD strip,
 * top-right of the playfield. Its y tracks the taller Sprint-10 HUD (+36).
 */
const LAUNCHER_TITLE: Rect = { x: 590, y: 128, w: 198, h: 36 };
/** The game-over "View Scoreboard" launcher button (inside the run-end strip). */
const LAUNCHER_GAMEOVER: Rect = { x: 250, y: 537, w: 300, h: 24 };

/** True when (x, y) lies inside `r`. */
function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** Time source for render-only animation — wall clock, never the sim. */
function now(): number {
  return performance.now() / 1000;
}

/** Ease-out cubic — for the run-end Flux count-up and node flashes. */
function easeOut(t: number): number {
  const u = clamp(t, 0, 1);
  return 1 - (1 - u) ** 3;
}

/** The Profile screen is reachable only between runs — never mid-run (§19.3). */
export function canOpenProfile(state: GameState): boolean {
  return (
    (state.phase === 'build' && state.wave === 0) ||
    state.phase === 'won' ||
    state.phase === 'lost'
  );
}

// --- the launcher button -----------------------------------------------------

/** The launcher button rect for the current screen, or null when not shown. */
function launcherRect(state: GameState): Rect | null {
  if (state.phase === 'build' && state.wave === 0) return LAUNCHER_TITLE;
  if (state.phase === 'won' || state.phase === 'lost') return LAUNCHER_GAMEOVER;
  return null;
}

/** True when (x, y) hits the Profile launcher button on the current screen. */
export function profileLauncherAt(x: number, y: number, state: GameState): boolean {
  const r = launcherRect(state);
  return r !== null && inRect(r, x, y);
}

/** Run-end Flux count-up animation state — re-armed each time a run settles. */
const fluxAnim = { value: -1, start: 0 };

/**
 * Draw the Profile launcher — a Flux readout + a button on the title screen,
 * and the run-end Flux award + a "View Scoreboard" button on the game-over
 * screen. `lastRunFlux` is the Flux the most recent run paid out (§19.1).
 */
export function drawProfileLauncher(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  lastRunFlux: number,
): void {
  if (state.phase === 'build' && state.wave === 0) {
    // Off the game-over screen — disarm the count-up so the next run re-plays it.
    fluxAnim.value = -1;
    drawTitleLauncher(ctx, LAUNCHER_TITLE, profile.flux);
    return;
  }
  if (state.phase === 'won' || state.phase === 'lost') {
    drawRunEndMeta(ctx, state, profile, lastRunFlux);
  }
}

/** The title-screen launcher — a violet Flux readout fused with a PROFILE button. */
function drawTitleLauncher(ctx: CanvasRenderingContext2D, r: Rect, flux: number): void {
  const t = now();
  const pulse = 0.5 + 0.5 * Math.sin(t * 2.2);
  ctx.save();

  // Body.
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
  g.addColorStop(0, 'rgba(20, 14, 36, 0.95)');
  g.addColorStop(1, 'rgba(10, 8, 20, 0.95)');
  ctx.fillStyle = g;
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(FLUX, 0.55 + 0.35 * pulse);
  ctx.lineWidth = 1.4;
  ctx.shadowBlur = 8 + 5 * pulse;
  ctx.shadowColor = FLUX;
  roundRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Flux mote + balance, left half.
  drawFluxMote(ctx, r.x + 18, r.y + r.h / 2, 6, t);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = `700 15px ${FONT_MONO}`;
  ctx.fillStyle = FLUX;
  ctx.shadowBlur = 6;
  ctx.shadowColor = FLUX;
  const fluxStr = String(flux);
  ctx.fillText(fluxStr, r.x + 30, r.y + r.h / 2 + 1);
  ctx.shadowBlur = 0;
  const numW = ctx.measureText(fluxStr).width;
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(FLUX, 0.7);
  ctx.fillText(tr('profile.flux'), r.x + 36 + numW, r.y + r.h / 2 + 1);

  // Divider + PROFILE label, right half.
  ctx.strokeStyle = withAlpha(FLUX, 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(r.x + r.w - 96, r.y + 8);
  ctx.lineTo(r.x + r.w - 96, r.y + r.h - 8);
  ctx.stroke();
  ctx.textAlign = 'center';
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.fillStyle = C.hudText;
  ctx.fillText(tr('profile.profileLauncher'), r.x + r.w - 60, r.y + r.h / 2 - 4);
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.accent, 0.85);
  ctx.fillText(tr('profile.profileKeyHint'), r.x + r.w - 60, r.y + r.h / 2 + 9);
  ctx.restore();
}

/** Measure a string under a given font without leaking ctx state. */
function measure(ctx: CanvasRenderingContext2D, text: string, font: string): number {
  ctx.save();
  ctx.font = font;
  const w = ctx.measureText(text).width;
  ctx.restore();
  return w;
}

/** A small four-point Flux spark — the electric-violet currency mote (§11). */
function drawFluxMote(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number): void {
  const spin = t * 0.8;
  const flick = 0.7 + 0.3 * Math.sin(t * 5);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(spin);
  ctx.fillStyle = FLUX;
  ctx.shadowBlur = 8 * flick;
  ctx.shadowColor = FLUX;
  ctx.beginPath();
  for (let i = 0; i < 4; i += 1) {
    const a = (i / 4) * Math.PI * 2;
    const a2 = a + Math.PI / 4;
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    ctx.lineTo(Math.cos(a2) * r * 0.34, Math.sin(a2) * r * 0.34);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * The run-end meta strip on the game-over screen (§20.3): the Flux earned
 * (counting up), the standing vs. the personal best ("NEW BEST ★" or the gap),
 * and the View Scoreboard launcher — all in a self-contained neon readout.
 */
function drawRunEndMeta(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  profile: Profile,
  lastRunFlux: number,
): void {
  const t = now();
  const cx = CONFIG.canvas.width / 2;
  const strip: Rect = { x: cx - 186, y: 486, w: 372, h: 80 };

  // Re-arm the count-up the first frame a fresh award appears.
  if (fluxAnim.value !== lastRunFlux) {
    fluxAnim.value = lastRunFlux;
    fluxAnim.start = t;
  }
  const prog = easeOut((t - fluxAnim.start) / 0.9);
  const shown = Math.round(lastRunFlux * prog);
  const counting = prog < 1;

  ctx.save();

  // Strip frame — a docked readout module, not a competing dialog.
  const g = ctx.createLinearGradient(0, strip.y, 0, strip.y + strip.h);
  g.addColorStop(0, 'rgba(18, 13, 32, 0.96)');
  g.addColorStop(1, 'rgba(9, 8, 18, 0.96)');
  ctx.fillStyle = g;
  roundRect(ctx, strip.x, strip.y, strip.w, strip.h, 9);
  ctx.fill();
  ctx.strokeStyle = withAlpha(FLUX, 0.5);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 14;
  ctx.shadowColor = FLUX;
  roundRect(ctx, strip.x, strip.y, strip.w, strip.h, 9);
  ctx.stroke();
  ctx.shadowBlur = 0;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Flux earned — counting up, mote on the left of the numeral.
  const glow = counting ? 8 + 8 * Math.abs(Math.sin(t * 9)) : 9;
  ctx.font = `700 19px ${FONT_MONO}`;
  const numStr = `+${shown}`;
  const numW = ctx.measureText(numStr).width;
  drawFluxMote(ctx, cx - numW / 2 - 30, strip.y + 21, 7, t);
  ctx.fillStyle = FLUX;
  ctx.shadowBlur = glow;
  ctx.shadowColor = FLUX;
  ctx.fillText(numStr, cx + 2, strip.y + 21);
  ctx.shadowBlur = 0;
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(FLUX, 0.8);
  // Sprint 69 — reuse-bug fix: profile.flux already in the catalogue.
  ctx.fillText(tr('profile.flux'), cx + 2 + numW / 2 + 18, strip.y + 22);

  // Standing vs. the personal best for this (mode, difficulty) — §20.3.
  // Sprint 30 — puzzle mode has its own per-level bests grid (not the
  // mode/difficulty Scoreboard), so the personal-best line here doesn't
  // apply; fall back to a no-best placeholder to keep TS happy. A puzzle run
  // shouldn't reach this overlay in MVP anyway (the level-select overlay
  // shows the puzzle-specific results).
  const best =
    state.mode === 'puzzle'
      ? { score: 0, wave: 0 }
      : bestFor(profile, state.mode, state.difficulty);
  const isBest = state.finalScore > 0 && state.finalScore >= best.score;
  if (isBest) {
    ctx.font = `700 12px ${FONT_MONO}`;
    ctx.fillStyle = GOLD;
    ctx.shadowBlur = 7;
    ctx.shadowColor = GOLD;
    ctx.fillText(tr('profile.newPersonalBest'), cx, strip.y + 43);
    ctx.shadowBlur = 0;
  } else {
    const gap = best.score - state.finalScore;
    ctx.font = `700 12px ${FONT_MONO}`;
    const lead = `${gap.toLocaleString()} `;
    const leadW = ctx.measureText(lead).width;
    ctx.font = `11px ${FONT_MONO}`;
    const tailLabel = tr('profile.fromYourBest');
    const tailW = ctx.measureText(tailLabel).width;
    const startX = cx - (leadW + tailW) / 2;
    ctx.textAlign = 'left';
    ctx.font = `700 12px ${FONT_MONO}`;
    ctx.fillStyle = C.accent;
    ctx.fillText(lead, startX, strip.y + 43);
    ctx.font = `11px ${FONT_MONO}`;
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(tailLabel, startX + leadW, strip.y + 43);
    ctx.textAlign = 'center';
  }

  ctx.restore();

  // View Scoreboard button, docked in the strip's lower band.
  drawLauncherButton(ctx, LAUNCHER_GAMEOVER, tr('profile.viewScoreboard'), tr('profile.profileKeyHint'));
}

/** A compact two-segment neon button — a label + a key hint. */
function drawLauncherButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  keyHint: string,
): void {
  const t = now();
  const pulse = 0.5 + 0.5 * Math.sin(t * 3);
  ctx.save();
  ctx.fillStyle = withAlpha(C.accent, 0.12);
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.5 + 0.35 * pulse);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 8 * pulse;
  ctx.shadowColor = C.accent;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.fillStyle = C.accent;
  ctx.fillText(label, r.x + r.w / 2 - 16, r.y + r.h / 2 + 1);
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudMuted, 0.95);
  ctx.fillText(keyHint, r.x + r.w - 30, r.y + r.h / 2 + 1);
  ctx.restore();
}

// --- the full Profile / Scoreboard overlay -----------------------------------

/**
 * Draw the full-screen Profile / Scoreboard overlay. `pointer` is the
 * current cursor position in canvas-space; when supplied, hovering a node
 * in the UNLOCKS tab pops up a one-line preview tooltip (REC 7, Sprint 18)
 * — so a locked node reads as a visible goal, not a mystery.
 */
export function drawProfileScreen(
  ctx: CanvasRenderingContext2D,
  profile: Profile,
  tab: ProfileTab,
  justFinished: boolean,
  pointer?: PointerPos,
): void {
  const { width, height } = CONFIG.canvas;
  const t = now();
  ctx.save();

  // Backdrop — darken the field, then a faint scanline texture.
  ctx.fillStyle = 'rgba(5, 7, 14, 0.95)';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
  for (let y = 0; y < height; y += 3) ctx.fillRect(0, y, width, 1);

  // Panel.
  const pg = ctx.createLinearGradient(0, PANEL.y, 0, PANEL.y + PANEL.h);
  pg.addColorStop(0, 'rgba(16, 22, 42, 0.99)');
  pg.addColorStop(1, 'rgba(9, 12, 24, 0.99)');
  ctx.fillStyle = pg;
  roundRect(ctx, PANEL.x, PANEL.y, PANEL.w, PANEL.h, 12);
  ctx.fill();
  ctx.strokeStyle = withAlpha(FLUX, 0.55 + 0.2 * Math.sin(t * 2));
  ctx.lineWidth = 1.5;
  ctx.shadowBlur = 20;
  ctx.shadowColor = FLUX;
  roundRect(ctx, PANEL.x, PANEL.y, PANEL.w, PANEL.h, 12);
  ctx.stroke();
  ctx.shadowBlur = 0;
  drawCornerBrackets(ctx, PANEL, FLUX);

  // Header — title block + Flux balance chip.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 22px ${FONT_DISPLAY}`;
  ctx.fillStyle = C.hudText;
  ctx.shadowBlur = 10;
  ctx.shadowColor = withAlpha(C.accent, 0.6);
  ctx.fillText(tr('profile.title'), PANEL.x + 28, PANEL.y + 38);
  ctx.shadowBlur = 0;
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.subtitle'), PANEL.x + 122, PANEL.y + 36);
  drawFluxChip(ctx, profile.flux, t);

  drawCloseButton(ctx);

  // Tabs.
  drawTab(ctx, TAB_UNLOCKS, tr('profile.tabUnlocks'), tab === 'unlocks');
  drawTab(ctx, TAB_SCORES, tr('profile.tabScoreboard'), tab === 'scoreboard');
  // Tab-strip baseline.
  ctx.strokeStyle = withAlpha(FLUX, 0.25);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PANEL.x + 28, TAB_UNLOCKS.y + TAB_UNLOCKS.h + 6);
  ctx.lineTo(PANEL.x + PANEL.w - 28, TAB_UNLOCKS.y + TAB_UNLOCKS.h + 6);
  ctx.stroke();

  if (tab === 'unlocks') drawUnlocksTab(ctx, profile, t, pointer);
  else drawScoreboardTab(ctx, profile, justFinished, t);

  // Footer hint.
  ctx.textAlign = 'center';
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.closeHint'), CONFIG.canvas.width / 2, PANEL.y + PANEL.h - 14);

  // Sprint 62 — Sign-out button shown only for signed-in players
  // (`Profile.guestMode === false`). Guests never see it; today the
  // backend is stubbed, so in practice it never renders this sprint.
  // The future backend swap simply makes `guestMode === false` reachable
  // and the row lights up for free.
  if (profile.guestMode === false) drawSignOutButton(ctx);
  ctx.restore();
}

/** Resolve a click inside the open overlay to a {@link ProfileHit}, or null. */
export function profileScreenHit(
  x: number,
  y: number,
  tab: ProfileTab,
  profile?: Profile,
): ProfileHit | null {
  if (inRect(CLOSE_RECT, x, y)) return { kind: 'close' };
  if (inRect(TAB_UNLOCKS, x, y)) return { kind: 'tab', tab: 'unlocks' };
  if (inRect(TAB_SCORES, x, y)) return { kind: 'tab', tab: 'scoreboard' };
  // Sprint 62 — the Sign-out button is reachable only when the player is
  // signed in. The `profile` argument is optional for backward
  // compatibility with the Sprint-9 call shape; callers that pass it get
  // the Sign-out hit, callers that don't get the legacy behaviour.
  if (profile && profile.guestMode === false && inRect(SIGNOUT_RECT, x, y)) {
    return { kind: 'signout' };
  }
  if (tab === 'unlocks') {
    for (const { id, rect } of unlockNodeRects()) {
      if (inRect(rect, x, y)) return { kind: 'unlock', id };
    }
  }
  return null;
}

/**
 * Sprint 62 — small "Sign out" button drawn at the bottom-left of the
 * Profile screen. Muted by design: it's a destination, not a primary
 * action. The orchestrator routes the click to {@link Game.signOut}.
 */
function drawSignOutButton(ctx: CanvasRenderingContext2D): void {
  const r = SIGNOUT_RECT;
  ctx.save();
  ctx.fillStyle = withAlpha(C.warning, 0.12);
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.warning, 0.65);
  ctx.lineWidth = 1.2;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.stroke();
  ctx.fillStyle = C.warning;
  ctx.font = `700 11px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(tr('auth.signOut'), r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.restore();
}

/** Decorative corner brackets — the OVERVOLT panel signature. */
function drawCornerBrackets(ctx: CanvasRenderingContext2D, r: Rect, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.4;
  const k = 16;
  const corner = (px: number, py: number, sx: number, sy: number): void => {
    ctx.beginPath();
    ctx.moveTo(px + sx * k, py);
    ctx.lineTo(px, py);
    ctx.lineTo(px, py + sy * k);
    ctx.stroke();
  };
  corner(r.x + 7, r.y + 7, 1, 1);
  corner(r.x + r.w - 7, r.y + 7, -1, 1);
  corner(r.x + 7, r.y + r.h - 7, 1, -1);
  corner(r.x + r.w - 7, r.y + r.h - 7, -1, -1);
  ctx.restore();
}

/** The header Flux balance chip — a luminous violet pill (§11). */
function drawFluxChip(ctx: CanvasRenderingContext2D, flux: number, t: number): void {
  const h = 30;
  const numStr = String(flux);
  const numW = measure(ctx, numStr, `700 17px ${FONT_MONO}`);
  const w = 40 + numW + 36;
  const x = CLOSE_RECT.x - 14 - w;
  const y = PANEL.y + 14;
  ctx.save();
  ctx.fillStyle = withAlpha(FLUX, 0.14);
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.strokeStyle = withAlpha(FLUX, 0.6);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 9;
  ctx.shadowColor = FLUX;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.stroke();
  ctx.shadowBlur = 0;
  drawFluxMote(ctx, x + 19, y + h / 2, 7, t);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = `700 17px ${FONT_MONO}`;
  ctx.fillStyle = FLUX;
  ctx.shadowBlur = 7;
  ctx.shadowColor = FLUX;
  ctx.fillText(numStr, x + 34, y + h / 2 + 1);
  ctx.shadowBlur = 0;
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(FLUX, 0.75);
  // Sprint 69 — reuse-bug fix: profile.flux already in the catalogue.
  ctx.fillText(tr('profile.flux'), x + 34 + numW + 6, y + h / 2 + 1);
  ctx.restore();
}

/** The close (X) button. */
function drawCloseButton(ctx: CanvasRenderingContext2D): void {
  const r = CLOSE_RECT;
  ctx.save();
  ctx.fillStyle = withAlpha(C.bad, 0.1);
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.bad, 0.8);
  ctx.lineWidth = 1.3;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.stroke();
  ctx.strokeStyle = C.bad;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(r.x + 8, r.y + 8);
  ctx.lineTo(r.x + r.w - 8, r.y + r.h - 8);
  ctx.moveTo(r.x + r.w - 8, r.y + 8);
  ctx.lineTo(r.x + 8, r.y + r.h - 8);
  ctx.stroke();
  ctx.restore();
}

/** One tab chip. */
function drawTab(ctx: CanvasRenderingContext2D, r: Rect, label: string, active: boolean): void {
  ctx.save();
  if (active) {
    const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    g.addColorStop(0, withAlpha(FLUX, 0.95));
    g.addColorStop(1, withAlpha(FLUX, 0.7));
    ctx.fillStyle = g;
    ctx.shadowBlur = 12;
    ctx.shadowColor = FLUX;
  } else {
    ctx.fillStyle = '#101a30';
  }
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = active ? FLUX : withAlpha(FLUX, 0.35);
  ctx.lineWidth = 1.2;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.stroke();
  ctx.fillStyle = active ? '#0a0a14' : C.hudMuted;
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.restore();
}

// --- UNLOCKS tab -------------------------------------------------------------

const BRANCH_ORDER = ['arsenal', 'modes', 'challenge', 'cosmetic'] as const;
/**
 * Per-branch display label — pulled live from the i18n catalogue. The branch
 * id stays the source-of-truth field; this map only resolves the heading.
 */
function branchLabel(branch: string): string {
  if (branch === 'arsenal') return tr('profile.branchArsenal');
  if (branch === 'modes') return tr('profile.branchModes');
  if (branch === 'challenge') return tr('profile.branchChallenge');
  if (branch === 'cosmetic') return tr('profile.branchCosmetic');
  return branch.toUpperCase();
}

/**
 * One-line preview text per unlock node — what it *does* and why a player
 * would want it. Surfaces this in the unlock-tree hover tooltip so a locked
 * node reads as a visible *goal*, not a mystery (REC 7, Sprint 18). Each
 * line mirrors the in-game role tooltips in src/ui/onboarding.ts where
 * applicable so the player learns the same vocabulary twice.
 */
/**
 * Map a runtime `CONFIG.unlocks[*].id` (dotted, e.g. `tower.mortar`) to its
 * camel-case i18n key suffix (`towerMortar`). The dot in the runtime id
 * collides with the `t()` dotted-path resolver, so the catalogue uses
 * camel-case (Sprint 69, i18n-gaps-extract-s69).
 */
function unlockKeyForId(id: string): string {
  // e.g. `tower.mortar` → `towerMortar`. Each `.x` becomes `X`.
  return id.replace(/\.(.)/g, (_m, c: string) => c.toUpperCase());
}

/**
 * One-line preview text per unlock node — what it *does* and why a player
 * would want it. Sprint 69 (i18n-gaps-extract-s69) routes this through
 * `t('profile.nodePreview.<key>')` so a locale switch updates the hover
 * tooltip without rebuild. The English defaults live in
 * `src/i18n/en.ts:profile.nodePreview`.
 */
function nodePreviewText(id: string): string {
  return t(`profile.nodePreview.${unlockKeyForId(id)}`);
}

/** Sprint 69 — localised display name for an unlock node id. */
function unlockNameText(id: string): string {
  return t(`unlocks.${unlockKeyForId(id)}.name`);
}
const NODES_TOP = PANEL.y + 140;
const NODE_W = 154;
const NODE_H = 60;
const NODE_GAP = 12;
const COL_GAP = 14;
const COL_START_X = PANEL.x + 31;

/** Newly-purchased nodes flash briefly — id → flash-start time (render-only). */
const purchaseFlash = new Map<string, number>();
/** The owned-set seen last frame, to detect a fresh purchase. */
let lastOwned: Set<string> | null = null;

/** Deterministic layout of every unlock node — shared by draw + hit-test. */
function unlockNodeRects(): { id: string; rect: Rect }[] {
  const out: { id: string; rect: Rect }[] = [];
  const colW = NODE_W + COL_GAP;
  BRANCH_ORDER.forEach((branch, col) => {
    const x = COL_START_X + col * colW;
    let y = NODES_TOP;
    for (const node of CONFIG.unlocks) {
      if (node.branch !== branch) continue;
      out.push({ id: node.id, rect: { x, y, w: NODE_W, h: NODE_H } });
      y += NODE_H + NODE_GAP;
    }
  });
  return out;
}

/** A short category tag for a node's grant — the node's second line. */
function grantTag(g: UnlockGrant): string {
  switch (g.kind) {
    case 'tower':
      return tr('profile.grantTower');
    case 'ability':
      return tr('profile.grantAbility');
    case 'mode':
      return tr('profile.grantMode');
    case 'difficulty':
      return tr('profile.grantDifficulty');
    case 'overload':
      return tr('profile.grantOverload', { tier: g.tier });
    case 'theme':
      return tr('profile.grantTheme');
  }
}

/** The UNLOCKS tab — the four-branch node graph + a lifetime-stats footer. */
function drawUnlocksTab(
  ctx: CanvasRenderingContext2D,
  profile: Profile,
  // Sprint 69 — renamed `t` → `anim` to free the local name for the i18n
  // helper; the parameter is the animation clock (purchase flash timing).
  anim: number,
  pointer: PointerPos | undefined,
): void {
  const rects = new Map(unlockNodeRects().map((n) => [n.id, n.rect]));
  const owned = new Set(profile.unlocked);
  const colW = NODE_W + COL_GAP;

  // Detect fresh purchases for the one-shot node flash; prune stale flashes.
  if (lastOwned !== null) {
    for (const id of owned) if (!lastOwned.has(id)) purchaseFlash.set(id, anim);
  }
  lastOwned = owned;
  for (const [id, start] of purchaseFlash) {
    if (anim - start >= 0.6) purchaseFlash.delete(id);
  }

  // Section title + unlock progress.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.unlockTree'), PANEL.x + 31, PANEL.y + 112);
  const total = CONFIG.unlocks.length;
  ctx.textAlign = 'right';
  ctx.font = `700 10px ${FONT_MONO}`;
  ctx.fillStyle = FLUX;
  ctx.fillText(
    tr('profile.unlockedCount', { owned: owned.size, total }),
    PANEL.x + PANEL.w - 31,
    PANEL.y + 112,
  );
  // Progress bar.
  const barW = 150;
  const barX = PANEL.x + PANEL.w - 31 - barW - 96;
  ctx.fillStyle = withAlpha(FLUX, 0.18);
  roundRect(ctx, barX, PANEL.y + 105, barW, 5, 2.5);
  ctx.fill();
  ctx.fillStyle = FLUX;
  ctx.shadowBlur = 6;
  ctx.shadowColor = FLUX;
  roundRect(ctx, barX, PANEL.y + 105, Math.max(2, (barW * owned.size) / total), 5, 2.5);
  ctx.fill();
  ctx.shadowBlur = 0;

  // Branch headers — a branch-coloured tick + label.
  ctx.textAlign = 'left';
  BRANCH_ORDER.forEach((branch, col) => {
    const x = COL_START_X + col * colW;
    const bc = BRANCH_COLOR[branch] ?? FLUX;
    ctx.fillStyle = bc;
    ctx.shadowBlur = 5;
    ctx.shadowColor = bc;
    ctx.fillRect(x, NODES_TOP - 17, 14, 3);
    ctx.shadowBlur = 0;
    ctx.font = `700 9px ${FONT_MONO}`;
    ctx.fillStyle = C.hudText;
    ctx.fillText(branchLabel(branch), x + 20, NODES_TOP - 13);
  });

  // Prerequisite links — a trace from a node up to its prereq; lit when met.
  for (const node of CONFIG.unlocks) {
    if (node.prereq === undefined) continue;
    const a = rects.get(node.id);
    const b = rects.get(node.prereq);
    if (!a || !b) continue;
    const lit = owned.has(node.prereq);
    const bc = BRANCH_COLOR[node.branch] ?? FLUX;
    drawPrereqLink(ctx, b, a, lit ? bc : withAlpha(C.hudMuted, 0.5), lit, anim);
  }

  // Nodes.
  for (const node of CONFIG.unlocks) {
    const rect = rects.get(node.id);
    if (!rect) continue;
    const isOwned = owned.has(node.id);
    const prereqMet = node.prereq === undefined || owned.has(node.prereq);
    const affordable = !isOwned && prereqMet && profile.flux >= node.cost;
    const prereqName =
      node.prereq !== undefined ? unlockNameText(node.prereq) || node.prereq : null;
    drawUnlockNode(
      ctx,
      rect,
      node.branch,
      // Sprint 69 — route node label through `t('unlocks.<key>.name')`.
      unlockNameText(node.id),
      node.cost,
      grantTag(node.grants),
      node.grants,
      {
        owned: isOwned,
        affordable,
        prereqMet,
        prereqName,
        flashAt: purchaseFlash.get(node.id),
        t: anim,
      },
    );
  }

  drawStatsFooter(ctx, profile);

  // Hover preview — draw after the nodes / footer so it sits on top of them.
  // This is the REC 7 deliverable: a locked node names *what it grants and
  // why a player would want it*, not just its branch tag and cost.
  if (pointer !== undefined) {
    for (const { id, rect } of unlockNodeRects()) {
      if (!inRect(rect, pointer.x, pointer.y)) continue;
      const node = CONFIG.unlocks.find((n) => n.id === id);
      if (!node) break;
      const isOwned = owned.has(node.id);
      const prereqMet = node.prereq === undefined || owned.has(node.prereq);
      const prereqName =
        node.prereq !== undefined ? unlockNameText(node.prereq) || node.prereq : null;
      drawNodePreview(ctx, rect, node, {
        owned: isOwned,
        affordable: !isOwned && prereqMet && profile.flux >= node.cost,
        prereqMet,
        prereqName,
        fluxShort: !isOwned && prereqMet ? Math.max(0, node.cost - profile.flux) : 0,
      });
      break;
    }
  }
}

/** Live status of the hovered node, for the preview tooltip. */
interface NodePreviewState {
  owned: boolean;
  affordable: boolean;
  prereqMet: boolean;
  prereqName: string | null;
  /** Flux still missing to afford the node (0 when affordable or owned). */
  fluxShort: number;
}

/**
 * Draw the hover tooltip for an unlock node — name, branch tag, grant icon,
 * a one-line {@link NODE_PREVIEW}, and a status line that names *why* it is
 * still locked (prereq / flux short) so the next step is legible. Positioned
 * right of the node when there is room, else left, and clamped inside the
 * panel.
 */
function drawNodePreview(
  ctx: CanvasRenderingContext2D,
  node: { x: number; y: number; w: number; h: number },
  unlock: { id: string; name: string; branch: string; cost: number; grants: UnlockGrant },
  s: NodePreviewState,
): void {
  const preview = nodePreviewText(unlock.id) || grantTag(unlock.grants);
  const bc = BRANCH_COLOR[unlock.branch] ?? FLUX;
  const w = 244;

  ctx.save();
  // Wrap the lines under the same font we will render with.
  ctx.font = `10px ${FONT_MONO}`;
  const previewLines = wrapText(ctx, preview, w - 24);
  // Status line — owned / affordable / locked-on-prereq / locked-on-flux.
  let status: { text: string; color: string };
  if (s.owned) {
    status = { text: tr('profile.statusUnlocked'), color: bc };
  } else if (!s.prereqMet && s.prereqName) {
    status = { text: tr('profile.statusRequiresPrereq', { prereq: s.prereqName }), color: C.warning };
  } else if (s.fluxShort > 0) {
    status = { text: tr('profile.statusFluxShort', { short: s.fluxShort }), color: C.warning };
  } else if (s.affordable) {
    status = { text: tr('profile.statusAffordable'), color: C.accent };
  } else {
    status = { text: tr('profile.statusCost', { cost: unlock.cost }), color: C.hudMuted };
  }
  const statusLines = wrapText(ctx, status.text, w - 24);

  // Measure card height: pad + header + tag line + preview + status.
  let h = 8 + 18 + 4; // top pad + header
  h += 14 + 6; // grant tag line
  h += previewLines.length * 14 + 4;
  h += 1 + 6 + statusLines.length * 13 + 8;

  // Position to the right of the node, else left, clamped inside PANEL.
  let x = node.x + node.w + 12;
  if (x + w > PANEL.x + PANEL.w - 8) x = node.x - w - 12;
  x = clamp(x, PANEL.x + 8, PANEL.x + PANEL.w - w - 8);
  let y = node.y;
  y = clamp(y, PANEL.y + 8, PANEL.y + PANEL.h - h - 8);

  // Card.
  ctx.fillStyle = 'rgba(10, 14, 30, 0.97)';
  roundRect(ctx, x, y, w, h, 8);
  ctx.fill();
  ctx.strokeStyle = withAlpha(bc, 0.85);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 14;
  ctx.shadowColor = withAlpha(bc, 0.55);
  roundRect(ctx, x, y, w, h, 8);
  ctx.stroke();
  ctx.shadowBlur = 0;
  // Branch-colour rail along the top.
  ctx.fillStyle = bc;
  roundRect(ctx, x, y, w, 3, 1.5);
  ctx.fill();

  const ix = x + 12;
  let cy = y + 10;

  // Header — name + cost.
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.fillStyle = bc;
  ctx.shadowBlur = 5;
  ctx.shadowColor = withAlpha(bc, 0.6);
  // Sprint 69 — route node label through `t('unlocks.<key>.name')`.
  ctx.fillText(unlockNameText(unlock.id) || unlock.name, ix, cy);
  ctx.shadowBlur = 0;
  ctx.textAlign = 'right';
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = s.owned ? bc : FLUX;
  ctx.fillText(s.owned ? tr('profile.statusOwned') : `✦${unlock.cost}`, x + w - 12, cy + 1);
  ctx.textAlign = 'left';
  cy += 22;

  // Grant tag — "WEAPON TOWER" / "ACTIVE ABILITY" / etc.
  ctx.font = `8.5px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(bc, 0.85);
  ctx.fillText(grantTag(unlock.grants), ix, cy);
  cy += 14;

  // Preview lines — the role / effect blurb.
  ctx.font = `10px ${FONT_MONO}`;
  ctx.fillStyle = C.hudText;
  for (const line of previewLines) {
    ctx.fillText(line, ix, cy);
    cy += 14;
  }
  cy += 4;

  // Divider + status line.
  ctx.strokeStyle = withAlpha(bc, 0.25);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(ix, cy);
  ctx.lineTo(x + w - 12, cy);
  ctx.stroke();
  cy += 7;
  ctx.font = `700 9.5px ${FONT_MONO}`;
  ctx.fillStyle = status.color;
  for (const line of statusLines) {
    ctx.fillText(line, ix, cy);
    cy += 13;
  }
  ctx.restore();
}

/** Greedily wrap `text` to lines no wider than `maxWidth` (ctx font preset). */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** A prerequisite trace between two stacked nodes; flowing dashes when lit. */
function drawPrereqLink(
  ctx: CanvasRenderingContext2D,
  from: Rect,
  to: Rect,
  color: string,
  lit: boolean,
  t: number,
): void {
  const x = from.x + from.w / 2;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lit ? 1.6 : 1;
  if (lit) {
    ctx.shadowBlur = 5;
    ctx.shadowColor = color;
    ctx.setLineDash([4, 4]);
    ctx.lineDashOffset = -(t * 14) % 8;
  }
  ctx.beginPath();
  ctx.moveTo(x, from.y + from.h);
  ctx.lineTo(x, to.y);
  ctx.stroke();
  ctx.restore();
}

interface NodeState {
  owned: boolean;
  affordable: boolean;
  prereqMet: boolean;
  prereqName: string | null;
  flashAt: number | undefined;
  /** Animation clock (seconds) — drives the affordable-node pulse + flash. */
  t: number;
}

/** One unlock-tree node tile — dim (locked) / cyan-outline (affordable) / filled (owned). */
function drawUnlockNode(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  branch: string,
  name: string,
  cost: number,
  tag: string,
  grant: UnlockGrant,
  s: NodeState,
): void {
  const bc = BRANCH_COLOR[branch] ?? FLUX;
  const locked = !s.owned && !s.affordable;
  ctx.save();
  if (locked) ctx.globalAlpha = 0.5;

  // Body.
  if (s.owned) {
    const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
    g.addColorStop(0, withAlpha(bc, 0.26));
    g.addColorStop(1, withAlpha(bc, 0.1));
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = '#0c1322';
  }
  roundRect(ctx, r.x, r.y, r.w, r.h, 7);
  ctx.fill();

  // Border — owned branch colour, affordable pulsing cyan, locked muted.
  let border = withAlpha(C.hudMuted, 0.55);
  let lw = 1.1;
  if (s.owned) {
    border = bc;
    lw = 1.5;
  } else if (s.affordable) {
    const pulse = 0.5 + 0.5 * Math.sin(s.t * 4);
    border = withAlpha(C.accent, 0.6 + 0.4 * pulse);
    lw = 1.8;
    ctx.shadowBlur = 6 + 6 * pulse;
    ctx.shadowColor = C.accent;
  }
  ctx.strokeStyle = border;
  ctx.lineWidth = lw;
  roundRect(ctx, r.x, r.y, r.w, r.h, 7);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Icon.
  const iconColor = locked ? C.hudMuted : bc;
  drawGrantIcon(ctx, grant.kind, r.x + 22, r.y + 21, iconColor);

  // Name (fitted to the available width).
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = locked ? C.hudMuted : C.hudText;
  drawFitText(ctx, name, r.x + 40, r.y + 19, r.w - 50, 11, `700 #SIZEpx ${FONT_DISPLAY}`);

  // Second line — the grant tag, or a prereq requirement when gated.
  ctx.font = `7.5px ${FONT_MONO}`;
  if (!s.owned && !s.prereqMet && s.prereqName) {
    ctx.fillStyle = withAlpha(C.warning, 0.95);
    drawFitText(ctx, tr('profile.nodeReq', { prereq: s.prereqName }), r.x + 40, r.y + 33, r.w - 50, 7.5, `#SIZEpx ${FONT_MONO}`);
  } else {
    ctx.fillStyle = locked ? withAlpha(C.hudMuted, 0.85) : withAlpha(bc, 0.9);
    ctx.fillText(tag, r.x + 40, r.y + 33);
  }

  // Bottom band — cost on the left, state badge on the right.
  ctx.font = `10px ${FONT_MONO}`;
  if (s.owned) {
    ctx.fillStyle = bc;
    ctx.fillText(tr('profile.statusUnlocked'), r.x + 12, r.y + r.h - 11);
    drawCheck(ctx, r.x + r.w - 17, r.y + r.h - 15, bc);
  } else {
    ctx.fillStyle = s.affordable ? C.accent : C.hudMuted;
    ctx.font = `9px ${FONT_MONO}`;
    ctx.fillText('✦', r.x + 12, r.y + r.h - 11);
    ctx.font = `700 11px ${FONT_MONO}`;
    ctx.fillText(String(cost), r.x + 24, r.y + r.h - 10);
    ctx.textAlign = 'right';
    if (s.affordable) {
      ctx.font = `700 10px ${FONT_DISPLAY}`;
      ctx.fillStyle = C.accent;
      ctx.fillText(tr('profile.buyAction'), r.x + r.w - 12, r.y + r.h - 10);
    } else {
      drawLockGlyph(ctx, r.x + r.w - 18, r.y + r.h - 15, C.hudMuted);
    }
    ctx.textAlign = 'left';
  }
  ctx.restore();

  // One-shot purchase flash — an expanding branch-coloured ring.
  if (s.flashAt !== undefined) {
    const age = s.t - s.flashAt;
    if (age >= 0 && age < 0.6) {
      const p = age / 0.6;
      ctx.save();
      ctx.globalAlpha = 1 - p;
      ctx.strokeStyle = bc;
      ctx.lineWidth = 2.5 * (1 - p);
      ctx.shadowBlur = 12 * (1 - p);
      ctx.shadowColor = bc;
      const grow = p * 10;
      roundRect(ctx, r.x - grow, r.y - grow, r.w + grow * 2, r.h + grow * 2, 7 + grow);
      ctx.stroke();
      ctx.restore();
    }
  }
}

/** Draw `text` at the largest size ≤ `maxSize` that fits `maxW`; `font` carries `#SIZE`. */
function drawFitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  maxSize: number,
  font: string,
): void {
  let size = maxSize;
  for (; size > 6; size -= 0.5) {
    ctx.font = font.replace('#SIZE', String(size));
    if (ctx.measureText(text).width <= maxW) break;
  }
  ctx.font = font.replace('#SIZE', String(size));
  ctx.fillText(text, x, y);
}

/** A small filled check mark inside a disc. */
function drawCheck(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.fillStyle = withAlpha(color, 0.22);
  ctx.beginPath();
  ctx.arc(cx, cy, 7.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.8;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 3.4, cy);
  ctx.lineTo(cx - 0.8, cy + 2.8);
  ctx.lineTo(cx + 3.6, cy - 3);
  ctx.stroke();
  ctx.restore();
}

/** A small padlock glyph for a locked, unaffordable node. */
function drawLockGlyph(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.arc(cx, cy - 1.5, 3, Math.PI, 0);
  ctx.stroke();
  roundRect(ctx, cx - 4.5, cy - 1.5, 9, 7, 1.5);
  ctx.fill();
  ctx.restore();
}

/** A small branch-coloured icon for a node's grant kind. */
function drawGrantIcon(
  ctx: CanvasRenderingContext2D,
  kind: UnlockGrant['kind'],
  cx: number,
  cy: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.shadowBlur = 4;
  ctx.shadowColor = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  switch (kind) {
    case 'tower': {
      // A turret silhouette — base disc + angled barrel.
      ctx.beginPath();
      ctx.arc(cx, cy + 2.5, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.save();
      ctx.translate(cx, cy + 2);
      ctx.rotate(-Math.PI / 4);
      roundRect(ctx, -1.7, -10, 3.4, 9, 1);
      ctx.fill();
      ctx.restore();
      break;
    }
    case 'ability': {
      // A lightning bolt.
      ctx.beginPath();
      ctx.moveTo(cx + 3, cy - 8);
      ctx.lineTo(cx - 5, cy + 1.5);
      ctx.lineTo(cx - 0.5, cy + 1.5);
      ctx.lineTo(cx - 3, cy + 8);
      ctx.lineTo(cx + 5, cy - 1.5);
      ctx.lineTo(cx + 0.5, cy - 1.5);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'mode': {
      // A branching route — three nodes joined by a zigzag trace.
      const pts: [number, number][] = [
        [cx - 7, cy + 5],
        [cx, cy - 5],
        [cx + 7, cy + 5],
      ];
      ctx.lineWidth = 1.7;
      ctx.beginPath();
      ctx.moveTo(pts[0]![0], pts[0]![1]);
      ctx.lineTo(pts[1]![0], pts[1]![1]);
      ctx.lineTo(pts[2]![0], pts[2]![1]);
      ctx.stroke();
      for (const [px, py] of pts) {
        ctx.beginPath();
        ctx.arc(px, py, 2.3, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'difficulty': {
      // Two stacked up-chevrons — "harder".
      ctx.lineWidth = 2;
      for (let i = 0; i < 2; i += 1) {
        const dy = cy - 3 + i * 6;
        ctx.beginPath();
        ctx.moveTo(cx - 6, dy + 4);
        ctx.lineTo(cx, dy - 1);
        ctx.lineTo(cx + 6, dy + 4);
        ctx.stroke();
      }
      break;
    }
    case 'overload': {
      // A warning triangle with an exclamation.
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 8);
      ctx.lineTo(cx + 8, cy + 6);
      ctx.lineTo(cx - 8, cy + 6);
      ctx.closePath();
      ctx.stroke();
      ctx.fillRect(cx - 1, cy - 3, 2, 5);
      ctx.beginPath();
      ctx.arc(cx, cy + 4, 1.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'theme': {
      // A paint swatch — a rounded square split by a lighter highlight.
      roundRect(ctx, cx - 7, cy - 7, 14, 14, 3);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.moveTo(cx - 7, cy + 7);
      ctx.lineTo(cx + 7, cy + 7);
      ctx.lineTo(cx + 7, cy - 7);
      ctx.closePath();
      ctx.fillStyle = withAlpha('#ffffff', 0.4);
      ctx.fill();
      break;
    }
  }
  ctx.restore();
}

/** The lifetime-stats footer of the UNLOCKS tab (§19.3) — five mini stat chips. */
function drawStatsFooter(ctx: CanvasRenderingContext2D, profile: Profile): void {
  const s = profile.stats;
  // The *true* deepest Endless wave — `stats.deepestEndlessWave` is tracked
  // independently of score (m2); `bests.endless[*].wave` would instead read the
  // best-*scoring* run's wave, understating a deep low-scoring run.
  const deepestEndless = s.deepestEndlessWave;
  const dividerY = PANEL.y + PANEL.h - 72;
  ctx.save();
  ctx.strokeStyle = withAlpha(FLUX, 0.25);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PANEL.x + 28, dividerY);
  ctx.lineTo(PANEL.x + PANEL.w - 28, dividerY);
  ctx.stroke();
  ctx.textAlign = 'left';
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.lifetime'), PANEL.x + 31, dividerY - 7);

  const cells: { label: string; value: string; color: string }[] = [
    { label: tr('profile.statRunsPlayed'), value: String(s.runsPlayed), color: C.hudText },
    { label: tr('profile.statCampaignWins'), value: String(s.campaignWins), color: C.good },
    { label: tr('profile.statBestScore'), value: s.bestScoreOverall.toLocaleString(), color: C.accent },
    { label: tr('profile.statDeepestEndless'), value: deepestEndless > 0 ? `W${deepestEndless}` : '—', color: C.warning },
    { label: tr('profile.statTotalFlux'), value: s.totalFluxEarned.toLocaleString(), color: FLUX },
  ];
  const cellW = (PANEL.w - 62) / cells.length;
  cells.forEach((cell, i) => {
    const x = PANEL.x + 31 + i * cellW;
    ctx.textAlign = 'left';
    ctx.font = `700 16px ${FONT_DISPLAY}`;
    ctx.fillStyle = cell.color;
    ctx.shadowBlur = 6;
    ctx.shadowColor = withAlpha(cell.color, 0.7);
    ctx.fillText(cell.value, x, dividerY + 24);
    ctx.shadowBlur = 0;
    ctx.font = `7.5px ${FONT_MONO}`;
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(cell.label, x, dividerY + 38);
  });
  ctx.restore();
}

// --- SCOREBOARD tab ----------------------------------------------------------

/** Mode / difficulty labels — resolved live from the i18n catalogue. */
function modeLabel(id: string): string {
  if (id === 'campaign' || id === 'endless' || id === 'maze' || id === 'puzzle') {
    return tr(`modes.${id}`);
  }
  return id.toUpperCase();
}
function diffLabel(id: string): string {
  if (id === 'easy' || id === 'normal' || id === 'hard') return tr(`difficulty.${id}`);
  return id.toUpperCase();
}

/** The SCOREBOARD tab — the personal-best grid + the run-history list (§20.2). */
function drawScoreboardTab(
  ctx: CanvasRenderingContext2D,
  profile: Profile,
  justFinished: boolean,
  t: number,
): void {
  ctx.textBaseline = 'alphabetic';

  // --- personal-best grid: rows = modes, columns = difficulties. -----------
  ctx.textAlign = 'left';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.personalBests'), PANEL.x + 31, PANEL.y + 112);

  const labelX = PANEL.x + 31;
  const gridX = PANEL.x + 168;
  const gridY = PANEL.y + 130;
  const cellW = 172;
  const cellH = 30;

  // Find the single best-scoring cell to crown it.
  let crownMode = '';
  let crownDiff = '';
  let crownScore = 0;
  for (const mode of RUN_MODES) {
    for (const d of DIFFICULTIES) {
      const e = profile.bests[mode][d];
      if (e && e.score > crownScore) {
        crownScore = e.score;
        crownMode = mode;
        crownDiff = d;
      }
    }
  }

  // Difficulty column headers.
  ctx.textAlign = 'center';
  ctx.font = `700 9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  DIFFICULTIES.forEach((d, c) => {
    ctx.fillText(diffLabel(d), gridX + c * cellW + cellW / 2, gridY - 8);
  });

  RUN_MODES.forEach((mode, row) => {
    const ry = gridY + row * cellH;
    ctx.textAlign = 'left';
    ctx.font = `700 11px ${FONT_DISPLAY}`;
    ctx.fillStyle = C.hudText;
    ctx.fillText(modeLabel(mode), labelX, ry + cellH / 2 + 4);

    DIFFICULTIES.forEach((d, c) => {
      const cx = gridX + c * cellW;
      const entry = profile.bests[mode][d];
      const locked = !isModeUnlocked(profile, mode) || !isDifficultyUnlocked(profile, d);
      const crowned = mode === crownMode && d === crownDiff && crownScore > 0;
      drawBestCell(ctx, cx + 3, ry + 3, cellW - 6, cellH - 6, entry, locked, crowned, t);
    });
  });

  // --- run-history list, newest first. -------------------------------------
  const history = profile.runHistory;
  const listTop = gridY + RUN_MODES.length * cellH + 24;
  ctx.textAlign = 'left';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(tr('profile.runHistory'), labelX, listTop);

  if (history.length === 0) {
    ctx.font = `11px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.hudMuted, 0.75);
    ctx.fillText(tr('profile.noRuns'), labelX, listTop + 26);
    return;
  }

  // Column header strip.
  const headY = listTop + 13;
  ctx.font = `7.5px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudMuted, 0.75);
  const w = PANEL.w - 62;
  ctx.textAlign = 'left';
  ctx.fillText(tr('profile.colRun'), labelX, headY);
  ctx.fillText(tr('profile.colMode'), labelX + 46, headY);
  ctx.fillText(tr('profile.colResult'), labelX + 232, headY);
  ctx.fillText(tr('profile.colOverload'), labelX + 312, headY);
  ctx.textAlign = 'right';
  ctx.fillText(tr('profile.colScore'), labelX + w - 150, headY);
  ctx.fillText(tr('profile.colDeltaBest'), labelX + w - 66, headY);
  ctx.fillText(tr('profile.colFlux'), labelX + w, headY);
  ctx.strokeStyle = withAlpha(C.hudMuted, 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(labelX, headY + 5);
  ctx.lineTo(labelX + w, headY + 5);
  ctx.stroke();

  // The single best-scoring run carries a star highlight.
  let bestRunIdx = 0;
  for (let i = 1; i < history.length; i += 1) {
    if (history[i]!.score > history[bestRunIdx]!.score) bestRunIdx = i;
  }
  const rowH = 15;
  const rowsTop = headY + 12;
  const maxRows = Math.min(history.length, 13);
  for (let i = 0; i < maxRows; i += 1) {
    const idx = history.length - 1 - i; // newest first
    const rec = history[idx]!;
    const justThis = justFinished && idx === history.length - 1;
    drawHistoryRow(
      ctx,
      labelX,
      rowsTop + i * rowH,
      rowH,
      w,
      rec,
      idx === bestRunIdx,
      justThis,
      profile,
      t,
    );
  }
  if (history.length > maxRows) {
    ctx.textAlign = 'center';
    ctx.font = `8px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.hudMuted, 0.7);
    const extra = history.length - maxRows;
    ctx.fillText(
      tr(extra === 1 ? 'profile.earlierRuns' : 'profile.earlierRunsPlural', { n: extra }),
      PANEL.x + PANEL.w / 2,
      rowsTop + maxRows * rowH + 9,
    );
  }
}

/** One personal-best grid cell — entry / locked / never-played, crowned if top. */
function drawBestCell(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  entry: { score: number; wave: number } | undefined,
  locked: boolean,
  crowned: boolean,
  t: number,
): void {
  ctx.save();
  // Cell body.
  ctx.fillStyle = crowned ? withAlpha(GOLD, 0.12) : '#0c1322';
  roundRect(ctx, x, y, w, h, 5);
  ctx.fill();
  if (crowned) {
    const pulse = 0.5 + 0.5 * Math.sin(t * 3);
    ctx.strokeStyle = withAlpha(GOLD, 0.55 + 0.35 * pulse);
    ctx.lineWidth = 1.3;
    ctx.shadowBlur = 7 * pulse;
    ctx.shadowColor = GOLD;
  } else {
    ctx.strokeStyle = withAlpha(entry ? C.accent : C.hudMuted, entry ? 0.3 : 0.18);
    ctx.lineWidth = 1;
  }
  roundRect(ctx, x, y, w, h, 5);
  ctx.stroke();
  ctx.shadowBlur = 0;

  const cx = x + w / 2;
  ctx.textAlign = 'center';
  if (entry && (entry.score > 0 || entry.wave > 0)) {
    // A migrated record can carry a wave with no score (m6) — show the wave
    // anyway, with a dash standing in for the unknown score.
    const scoreStr = entry.score > 0 ? entry.score.toLocaleString() : '—';
    // The crowned cell shifts its numeral right to clear the leading star.
    ctx.font = `700 14px ${FONT_MONO}`;
    const numW = ctx.measureText(scoreStr).width;
    const offset = crowned ? 7 : 0;
    ctx.fillStyle = crowned ? GOLD : C.accent;
    ctx.fillText(scoreStr, cx + offset, y + h / 2 - 1);
    if (crowned) {
      ctx.font = `10px ${FONT_MONO}`;
      ctx.fillStyle = GOLD;
      ctx.shadowBlur = 4;
      ctx.shadowColor = GOLD;
      ctx.textAlign = 'right';
      // Right-aligned so the star sits a clean 5px left of the numeral.
      ctx.fillText('★', cx + offset - numW / 2 - 5, y + h / 2 - 1);
      ctx.textAlign = 'center';
      ctx.shadowBlur = 0;
    }
    ctx.font = `7.5px ${FONT_MONO}`;
    ctx.fillStyle = C.hudMuted;
    ctx.fillText(tr('profile.waveLabel', { wave: entry.wave }), cx, y + h / 2 + 11);
  } else if (locked) {
    drawLockGlyph(ctx, cx, y + h / 2 - 3, withAlpha(C.hudMuted, 0.8));
    ctx.font = `7px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.hudMuted, 0.7);
    ctx.fillText(tr('hud.locked'), cx, y + h / 2 + 11);
  } else {
    ctx.font = `13px ${FONT_MONO}`;
    ctx.fillStyle = withAlpha(C.hudMuted, 0.55);
    ctx.fillText('—', cx, y + h / 2 + 3);
  }
  ctx.restore();
}

/** One run-history row. */
function drawHistoryRow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  h: number,
  w: number,
  rec: RunRecord,
  isBest: boolean,
  justFinished: boolean,
  profile: Profile,
  t: number,
): void {
  ctx.save();

  // Row backing — the just-finished run pulses; the best run gets a faint tint.
  if (justFinished) {
    const pulse = 0.5 + 0.5 * Math.sin(t * 3);
    ctx.fillStyle = withAlpha(FLUX, 0.1 + 0.12 * pulse);
    roundRect(ctx, x - 5, y - 1, w + 10, h - 1, 4);
    ctx.fill();
    ctx.strokeStyle = withAlpha(FLUX, 0.4);
    ctx.lineWidth = 1;
    roundRect(ctx, x - 5, y - 1, w + 10, h - 1, 4);
    ctx.stroke();
  } else if (isBest) {
    ctx.fillStyle = withAlpha(GOLD, 0.07);
    roundRect(ctx, x - 5, y - 1, w + 10, h - 1, 4);
    ctx.fill();
  }

  ctx.textBaseline = 'middle';
  const cy = y + h / 2;
  ctx.font = `9.5px ${FONT_MONO}`;

  // Run index.
  ctx.textAlign = 'left';
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(`#${rec.run}`, x, cy);

  // Mode · difficulty.
  ctx.fillStyle = C.hudText;
  ctx.fillText(`${modeLabel(rec.mode)} · ${diffLabel(rec.difficulty)}`, x + 46, cy);

  // Result chip — green win / red loss.
  const resultColor = rec.won ? C.good : C.bad;
  const resultText = `W${rec.wavesCleared} ${rec.won ? '✓' : '✗'}`;
  ctx.font = `700 9px ${FONT_MONO}`;
  const chipW = ctx.measureText(resultText).width + 12;
  ctx.fillStyle = withAlpha(resultColor, 0.16);
  roundRect(ctx, x + 232, cy - 7, chipW, 14, 4);
  ctx.fill();
  ctx.fillStyle = resultColor;
  ctx.textAlign = 'left';
  ctx.fillText(resultText, x + 238, cy + 1);

  // Overload badge.
  if (rec.overloadMax > 0) {
    ctx.font = `700 9px ${FONT_MONO}`;
    ctx.fillStyle = C.warning;
    ctx.fillText(`⚡${rec.overloadMax}`, x + 312, cy);
  }

  // Score, the delta vs. that (mode, difficulty)'s best, and Flux.
  ctx.font = `700 10px ${FONT_MONO}`;
  ctx.textAlign = 'right';
  ctx.fillStyle = C.accent;
  ctx.fillText(rec.score.toLocaleString(), x + w - 150, cy);

  const best = bestFor(profile, rec.mode, rec.difficulty);
  ctx.font = `9px ${FONT_MONO}`;
  if (isBest) {
    ctx.fillStyle = GOLD;
    ctx.shadowBlur = 5;
    ctx.shadowColor = GOLD;
    ctx.fillText(tr('profile.starBest'), x + w - 66, cy);
    ctx.shadowBlur = 0;
  } else {
    const gap = Math.max(0, best.score - rec.score);
    ctx.fillStyle = withAlpha(C.hudMuted, 0.95);
    ctx.fillText(gap > 0 ? `−${gap.toLocaleString()}` : '—', x + w - 66, cy);
  }

  ctx.font = `700 9.5px ${FONT_MONO}`;
  ctx.fillStyle = FLUX;
  ctx.fillText(`+${rec.fluxEarned} ✦`, x + w, cy);
  ctx.restore();
}
